import { useApp } from '@/contexts';

import { useMemo, useState } from 'react';

import { Download as DownloadIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { camelCase } from 'lodash';
import { useSearchParams } from 'react-router';

import { PageContainer } from '@/components/layout';

import { useGetSfJobSuccessResultsQuery } from '../api/transformersApi';

/**
 * Salesforce Bulk Job Successful Results page
 * Shows detailed successful record results for a specific Salesforce bulk job
 *
 * URL: /a/:appId/health/sfjobs/successful?jobId=xxx&env=xxx&connectionKey=xxx
 */
export const SfJobSuccessResultsPage = () => {
  const { appId, tenantId } = useApp();
  const [searchParams] = useSearchParams();

  const jobId = searchParams.get('jobId');
  const envId = searchParams.get('env');
  const connectionKey = searchParams.get('connectionKey') || 'defaultConnection';

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Fetch successful results
  const {
    data: results,
    isLoading,
    error,
  } = useGetSfJobSuccessResultsQuery(
    {
      tenantId: String(tenantId),
      appId: String(appId),
      envId: String(envId),
      connectionKey,
      jobId: jobId!,
    },
    { skip: !tenantId || !appId || !envId || !jobId }
  );

  // Transform results to lowercase keys (matching dataProtect behavior)
  const formattedResults = useMemo(() => {
    if (!results) return [];
    return results.map((row) => {
      const lowerCaseRow: Record<string, unknown> = {};
      Object.keys(row).forEach((key) => {
        lowerCaseRow[key.toLowerCase()] = row[key];
      });
      return lowerCaseRow;
    });
  }, [results]);

  // Extract headers dynamically
  const headers = useMemo(() => {
    const headerSet = new Set<string>();
    formattedResults.forEach((row) => {
      Object.keys(row).forEach((key) => headerSet.add(key));
    });
    return Array.from(headerSet);
  }, [formattedResults]);

  // Paginated data
  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    return formattedResults.slice(start, start + rowsPerPage);
  }, [formattedResults, page, rowsPerPage]);

  // CSV download handler
  const handleDownloadCSV = () => {
    if (!formattedResults.length) return;

    const csvHeaders = headers.join(',');
    const csvRows = formattedResults.map((row) =>
      headers
        .map((h) => {
          const val = row[h];
          const strVal = val === null || val === undefined ? '' : String(val);
          // Escape quotes and wrap in quotes if contains comma or quote
          if (strVal.includes(',') || strVal.includes('"') || strVal.includes('\n')) {
            return `"${strVal.replace(/"/g, '""')}"`;
          }
          return strVal;
        })
        .join(',')
    );
    const csvContent = [csvHeaders, ...csvRows].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'SF_Success_Report.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!jobId || !envId) {
    return (
      <PageContainer>
        <Typography variant="h6" sx={{ fontSize: 14, fontWeight: 600, mb: 2 }}>
          Salesforce Bulk Job Successful Record Results
        </Typography>
        <Alert severity="warning">
          Missing required parameters. Please access this page from a bulk jobs info view.
        </Alert>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <Typography variant="h6" sx={{ fontSize: 14, fontWeight: 600, mb: 2 }}>
          Salesforce Bulk Job Successful Record Results - {jobId}
        </Typography>
        <Alert severity="error">Failed to load successful results.</Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography
          variant="h6"
          sx={{
            fontSize: 14,
            fontWeight: 600,
            color: '#333',
          }}
        >
          Salesforce Bulk Job Successful Record Results - {jobId}
        </Typography>
        {formattedResults.length > 0 && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadCSV}
          >
            Download CSV
          </Button>
        )}
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading successful results...</Typography>
        </Box>
      ) : formattedResults.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No successful records found.
        </Typography>
      ) : (
        <>
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ maxHeight: '80vh', overflowX: 'auto', overflowY: 'auto' }}
          >
            <Table size="small" stickyHeader sx={{ tableLayout: 'auto' }}>
              <TableHead>
                <TableRow>
                  {headers.map((header) => (
                    <TableCell
                      key={header}
                      sx={{
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        fontSize: 13,
                        backgroundColor: '#f5f5f5',
                      }}
                    >
                      {camelCase(header)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((row, index) => (
                  <TableRow key={index}>
                    {headers.map((header) => (
                      <TableCell key={header} sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                        {String(row[header] ?? '')}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={formattedResults.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 15, 25, 50]}
          />
        </>
      )}
    </PageContainer>
  );
};
